import { Board } from '../game/board';
import { CellState, Position, Direction, LineCheckResult } from '../types';
import { DIRECTIONS, getOppositeDirection } from './patterns';

/**
 * Checks for consecutive marks in a specific direction
 */
export function countConsecutiveInDirection(
  board: Board,
  position: Position,
  direction: Direction,
  mark: CellState
): LineCheckResult {
  const positions: Position[] = [position];
  let count = 1;
  let currentRow = position.row + direction.rowDelta;
  let currentCol = position.col + direction.colDelta;

  // Count in the positive direction
  while (
    board.isValidPosition(currentRow, currentCol) &&
    board.getCell(currentRow, currentCol) === mark
  ) {
    positions.push({ row: currentRow, col: currentCol });
    count++;
    currentRow += direction.rowDelta;
    currentCol += direction.colDelta;
  }

  // Check if there's an open end in the positive direction
  const hasOpenEnd =
    board.isValidPosition(currentRow, currentCol) &&
    board.getCell(currentRow, currentCol) === CellState.EMPTY;

  // Count in the opposite direction
  const oppositeDir = getOppositeDirection(direction);
  currentRow = position.row + oppositeDir.rowDelta;
  currentCol = position.col + oppositeDir.colDelta;

  while (
    board.isValidPosition(currentRow, currentCol) &&
    board.getCell(currentRow, currentCol) === mark
  ) {
    positions.unshift({ row: currentRow, col: currentCol });
    count++;
    currentRow += oppositeDir.rowDelta;
    currentCol += oppositeDir.colDelta;
  }

  // Check if there's an open end in the negative direction
  const hasOpenStart =
    board.isValidPosition(currentRow, currentCol) &&
    board.getCell(currentRow, currentCol) === CellState.EMPTY;

  return {
    count,
    positions,
    hasOpenStart,
    hasOpenEnd,
  };
}

/**
 * Checks if a winning line has at least one open end (Vietnamese Caro rule)
 */
export function hasOpenEnd(lineResult: LineCheckResult): boolean {
  return lineResult.hasOpenStart || lineResult.hasOpenEnd;
}

/**
 * Checks if the move at the given position results in a win
 * Implements the Vietnamese Caro rule: line must have at least one open end
 */
export function checkWin(
  board: Board,
  position: Position,
  mark: CellState,
  winLength: number = 5,
  requireOpenEnd: boolean = true
): boolean {
  // Check all four directions
  for (const direction of DIRECTIONS) {
    const lineResult = countConsecutiveInDirection(
      board,
      position,
      direction,
      mark
    );

    // Check if we have enough consecutive marks
    if (lineResult.count >= winLength) {
      // If Vietnamese Caro rule is enabled, check for open end
      if (requireOpenEnd) {
        if (hasOpenEnd(lineResult)) {
          return true;
        }
      } else {
        // Standard Gomoku - just need 5 in a row
        return true;
      }
    }
  }

  return false;
}

/**
 * Validates if a position is a valid move
 */
export function isValidMove(board: Board, row: number, col: number): boolean {
  return board.isValidPosition(row, col) && board.isCellEmpty(row, col);
}
